import { BellRinging } from "@phosphor-icons/react";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import PushToggle from "@/components/parent/PushToggle";
import PWAInstallCard from "@/components/PWAInstallCard";
import {
  getPreferences,
  upsertPreferences,
} from "@/lib/notifications";
import { useCallback, useEffect, useState } from "react";

/**
 * Teacher · Preferences.
 *
 * Narrow on purpose: teachers don't receive the parent-facing
 * notification kinds (daily reports to own children, invoices, etc.),
 * so we only surface the two controls they care about:
 *
 *   1. "Install" prompt (PWA)  — makes the mobile app feel native
 *      and is the prerequisite for push delivery on iOS.
 *   2. New-message push toggle — re-uses the same PushToggle FSM
 *      parents see, wired to the same `new_message_push` column in
 *      notification_preferences.
 *
 * Parity with parent here is intentional: teachers are the other
 * half of the messaging loop, and push on both sides is the single
 * biggest mobile-engagement win we can ship without new infra.
 */
export default function TeacherPreferences() {
  const { status, profile } = useAuthGate();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (status !== "ready") {
      if (status === "session_expired") setLoading(false);
      return;
    }
    try {
      const p = await getPreferences(profile.id);
      setPrefs(p);
    } finally {
      setLoading(false);
    }
  }, [status, profile?.id]);

  useEffect(() => { load(); }, [load]);

  if (status === "session_expired") {
    return <SessionExpiredState scope="preferences" testId="teacher-preferences-session-expired" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
      </div>
    );
  }

  // Default-on if the row doesn't exist yet — matches the RPC behaviour.
  const p = prefs || { new_message_push: true };

  return (
    <div data-testid="teacher-preferences" className="space-y-6">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          Teacher
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Notifications & app
        </h1>
        <p className="mt-2 max-w-xl text-sm text-neutral-600">
          Stay reachable while you're on the floor. Install the app to your
          home screen and decide when your phone should buzz.
        </p>
      </header>

      <PWAInstallCard />

      <section className="rounded-2xl border border-neutral-200 bg-white p-5">
        <p className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
          <BellRinging size={14} weight="duotone" className="text-primary-hover" />
          Push notifications
        </p>
        <PushToggle
          prefOn={!!p.new_message_push}
          onPrefChange={async (on) => {
            setSaving(true);
            const next = { ...p, new_message_push: on };
            setPrefs(next);
            try {
              await upsertPreferences(profile.id, { new_message_push: on });
            } catch (err) {
              setPrefs(p);
              throw err;
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving}
        />
        <p className="mt-3 text-[11px] leading-relaxed text-neutral-500">
          You'll get a push when a parent or admin messages you. Tap the
          notification to jump straight to the conversation.
        </p>
      </section>
    </div>
  );
}
